import { decideCraApplicabilityStatusFromAnswers } from '@artem-lex/common-server/productApplicability/craApplicability'
import type {
  LawQuestionnaireCategoryRule,
  LawQuestionnaireQuestion,
  LawQuestionnaireQuestionGroup,
  ProductLawApplicabilityStatus,
} from '@/shared/hooks/api/product'

export type LawQuestionnaireAnswerDraft = Record<string, boolean | null>

export type LawQuestionnaireContribution =
  | 'decided'
  | 'required'
  | 'checkedClear'
  | 'unused'
  | 'conflict'
  | 'needed'

export type LawQuestionnaireClauseState = 'pass' | 'fail' | 'pending'

export type LawQuestionnaireGroupedQuestions = {
  group: LawQuestionnaireQuestionGroup
  questions: LawQuestionnaireQuestion[]
}

const QUESTION_GROUP_ORDER: LawQuestionnaireQuestionGroup[] = [
  'inScope',
  'transitional',
  'categories',
  'exclusions',
]

export function buildDraftFromQuestions(
  questions: LawQuestionnaireQuestion[]
): LawQuestionnaireAnswerDraft {
  const draft: LawQuestionnaireAnswerDraft = {}
  for (const question of questions) {
    draft[question.questionKey] = question.answer
  }
  return draft
}

export function isQuestionUnanswered(
  question: LawQuestionnaireQuestion,
  draft: LawQuestionnaireAnswerDraft
): boolean {
  return getQuestionDraftAnswer(question, draft) === null
}

export function getQuestionDraftAnswer(
  question: LawQuestionnaireQuestion,
  draft: LawQuestionnaireAnswerDraft
): boolean | null {
  if (Object.prototype.hasOwnProperty.call(draft, question.questionKey)) {
    return draft[question.questionKey] ?? null
  }
  return question.answer
}

export function getVisibleLawQuestionnaireQuestions(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  pendingOnly: boolean
): {
  questions: LawQuestionnaireQuestion[]
  reviewAllAnswered: boolean
  unansweredCount: number
} {
  const unansweredCount = questions.filter((question) =>
    isQuestionUnanswered(question, draft)
  ).length

  if (!pendingOnly) {
    return { questions, reviewAllAnswered: false, unansweredCount }
  }

  const reviewAllAnswered =
    questions.length > 0 && unansweredCount === 0

  return { questions, reviewAllAnswered, unansweredCount }
}

export function getPendingQuestionnaireNoticeKey(
  pendingOnly: boolean,
  unansweredCount: number,
  liveStatus: ProductLawApplicabilityStatus
):
  | 'v2.applicability.pendingQuestions.description'
  | 'v2.applicability.pendingQuestions.allAnswered'
  | 'v2.applicability.pendingQuestions.reviewAllAnswered'
  | null {
  if (!pendingOnly) {
    return null
  }
  if (unansweredCount > 0) {
    return 'v2.applicability.pendingQuestions.description'
  }
  if (liveStatus === 'APPLICABLE' || liveStatus === 'NOT_APPLICABLE') {
    return 'v2.applicability.pendingQuestions.allAnswered'
  }
  return 'v2.applicability.pendingQuestions.reviewAllAnswered'
}

export function groupLawQuestionnaireQuestions(
  questions: LawQuestionnaireQuestion[]
): LawQuestionnaireGroupedQuestions[] {
  return QUESTION_GROUP_ORDER.flatMap((group) => {
    const groupedQuestions = questions.filter(
      (question) => question.group === group
    )
    if (groupedQuestions.length === 0) {
      return []
    }
    return [{ group, questions: groupedQuestions }]
  })
}

function questionHasGap(
  question: LawQuestionnaireQuestion,
  draft: LawQuestionnaireAnswerDraft
): boolean {
  if (Object.prototype.hasOwnProperty.call(draft, question.questionKey)) {
    return false
  }
  return question.hasGap
}

function isPackIncomplete(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): boolean {
  return questions.some((question) => {
    if (getQuestionDraftAnswer(question, draft) === null) {
      return true
    }
    return questionHasGap(question, draft)
  })
}

function countAnswers(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  expected: boolean | null
): number {
  return questions.filter((question) => {
    return getQuestionDraftAnswer(question, draft) === expected
  }).length
}

function getInScopeClauseState(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): LawQuestionnaireClauseState {
  if (countAnswers(questions, draft, false) > 0) {
    return 'fail'
  }
  if (countAnswers(questions, draft, null) > 0 || questions.length === 0) {
    return 'pending'
  }
  return 'pass'
}

function getCategoryClauseState(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule
): LawQuestionnaireClauseState {
  if (questions.length === 0 || categoryRule == null) {
    return 'pass'
  }

  const yesCount = countAnswers(questions, draft, true)
  const unansweredCount = countAnswers(questions, draft, null)

  if (categoryRule === 'any') {
    if (yesCount > 0) {
      return 'pass'
    }
    return 'pending'
  }

  if (yesCount > 1) {
    return 'fail'
  }
  if (yesCount === 1 && unansweredCount === 0) {
    return 'pass'
  }
  return 'pending'
}

function getExclusionClauseState(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): LawQuestionnaireClauseState {
  if (countAnswers(questions, draft, true) > 0) {
    return 'fail'
  }
  if (countAnswers(questions, draft, null) > 0) {
    return 'pending'
  }
  return 'pass'
}

export function getLawQuestionnaireClauseState(
  group: LawQuestionnaireQuestionGroup,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule
): LawQuestionnaireClauseState {
  if (group === 'inScope') {
    return getInScopeClauseState(questions, draft)
  }
  if (group === 'categories') {
    return getCategoryClauseState(questions, draft, categoryRule)
  }
  return getExclusionClauseState(questions, draft)
}

export function getCraClearAnswer(
  question: LawQuestionnaireQuestion,
  draft: LawQuestionnaireAnswerDraft
): boolean | null {
  const draftValue = getQuestionDraftAnswer(question, draft)
  const draftOverridesStoredAnswer =
    Object.prototype.hasOwnProperty.call(draft, question.questionKey) &&
    draft[question.questionKey] !== question.answer
  if (question.hasGap && !draftOverridesStoredAnswer) {
    return null
  }
  return draftValue
}

function craAnswersFromQuestionnaire(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): Record<string, unknown> {
  const answers: Record<string, unknown> = {}
  for (const question of questions) {
    const draftOverridesStoredAnswer =
      Object.prototype.hasOwnProperty.call(draft, question.questionKey) &&
      draft[question.questionKey] !== question.answer
    if (question.hasGap && !draftOverridesStoredAnswer) {
      answers[question.questionKey] = question.answer
      answers[`${question.questionKey}_hasGap`] = true
      continue
    }
    answers[question.questionKey] = getQuestionDraftAnswer(question, draft)
  }
  return answers
}

function isCraQuestionnaire(questions: LawQuestionnaireQuestion[]): boolean {
  return questions.some((question) => question.group === 'transitional')
}

export function getLiveLawQuestionnaireStatus(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule,
  requiresCompletePackBeforeVerdict = false
): ProductLawApplicabilityStatus {
  if (isCraQuestionnaire(questions)) {
    return decideCraApplicabilityStatusFromAnswers(
      craAnswersFromQuestionnaire(questions, draft)
    )
  }

  if (
    requiresCompletePackBeforeVerdict &&
    isPackIncomplete(questions, draft)
  ) {
    return 'NEEDS_GRILLING'
  }

  const grouped = groupLawQuestionnaireQuestions(questions)
  const inScope =
    grouped.find((entry) => entry.group === 'inScope')?.questions ?? []
  const categories =
    grouped.find((entry) => entry.group === 'categories')?.questions ?? []
  const exclusions =
    grouped.find((entry) => entry.group === 'exclusions')?.questions ?? []

  const inScopeState = getInScopeClauseState(inScope, draft)
  if (inScopeState === 'fail') {
    return 'NOT_APPLICABLE'
  }

  const exclusionState = getExclusionClauseState(exclusions, draft)
  if (exclusionState === 'fail') {
    return 'NOT_APPLICABLE'
  }

  const categoryState = getCategoryClauseState(categories, draft, categoryRule)
  if (
    inScopeState === 'pass' &&
    exclusionState === 'pass' &&
    categoryState === 'pass'
  ) {
    return 'APPLICABLE'
  }

  return 'NEEDS_GRILLING'
}

function getGateContribution(
  answer: boolean | null
): LawQuestionnaireContribution {
  if (answer === false) {
    return 'decided'
  }
  if (answer === true) {
    return 'required'
  }
  return 'needed'
}

function getCategoryContribution(
  answer: boolean | null,
  yesCount: number,
  categoryRule: LawQuestionnaireCategoryRule,
  resultAlreadyDecidedOut: boolean
): LawQuestionnaireContribution {
  if (resultAlreadyDecidedOut) {
    return 'unused'
  }
  if (categoryRule === 'exactlyOne' && yesCount > 1 && answer === true) {
    return 'conflict'
  }
  if (answer === true) {
    return 'required'
  }
  if (yesCount > 0 && answer === false) {
    return 'unused'
  }
  if (answer === false) {
    return 'needed'
  }
  return 'needed'
}

function getExclusionContribution(
  answer: boolean | null,
  resultAlreadyDecidedOutByGate: boolean,
  anyExclusionYes: boolean
): LawQuestionnaireContribution {
  if (answer === true) {
    return 'decided'
  }
  if (resultAlreadyDecidedOutByGate) {
    return 'unused'
  }
  if (answer === false) {
    return 'checkedClear'
  }
  if (anyExclusionYes) {
    return 'unused'
  }
  return 'needed'
}

function getCraContribution(
  question: LawQuestionnaireQuestion,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft
): LawQuestionnaireContribution {
  const answer = getCraClearAnswer(question, draft)
  const verdict = decideCraApplicabilityStatusFromAnswers(
    craAnswersFromQuestionnaire(questions, draft)
  )
  const decidesNotApplicable =
    (question.group === 'inScope' && answer === false) ||
    (question.group !== 'inScope' && answer === true)

  if (decidesNotApplicable) {
    return 'decided'
  }
  if (verdict === 'NOT_APPLICABLE' && answer === null) {
    return 'unused'
  }
  if (question.group === 'inScope') {
    if (answer === true) {
      return 'required'
    }
    return 'needed'
  }
  if (answer === false) {
    return 'checkedClear'
  }
  return 'needed'
}

export function getLawQuestionnaireContribution(
  question: LawQuestionnaireQuestion,
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule
): LawQuestionnaireContribution {
  if (isCraQuestionnaire(questions)) {
    return getCraContribution(question, questions, draft)
  }

  const answer = getQuestionDraftAnswer(question, draft)
  const grouped = groupLawQuestionnaireQuestions(questions)
  const inScope =
    grouped.find((entry) => entry.group === 'inScope')?.questions ?? []
  const categories =
    grouped.find((entry) => entry.group === 'categories')?.questions ?? []
  const exclusions =
    grouped.find((entry) => entry.group === 'exclusions')?.questions ?? []
  const gateFailed = getInScopeClauseState(inScope, draft) === 'fail'
  const anyExclusionYes = countAnswers(exclusions, draft, true) > 0

  if (question.group === 'inScope') {
    return getGateContribution(answer)
  }

  if (question.group === 'categories') {
    const yesCount = countAnswers(categories, draft, true)
    return getCategoryContribution(
      answer,
      yesCount,
      categoryRule,
      gateFailed || anyExclusionYes
    )
  }

  return getExclusionContribution(answer, gateFailed, anyExclusionYes)
}

export function contributionBadgeVariant(
  contribution: LawQuestionnaireContribution
): 'success' | 'warning' | 'outline' | 'secondary' | 'danger' {
  if (contribution === 'decided') {
    return 'danger'
  }
  if (contribution === 'required') {
    return 'success'
  }
  if (contribution === 'conflict' || contribution === 'needed') {
    return 'warning'
  }
  if (contribution === 'checkedClear') {
    return 'outline'
  }
  return 'secondary'
}

export function contributionCardClassName(
  contribution: LawQuestionnaireContribution
): string {
  if (contribution === 'decided') {
    return 'border-destructive/40 bg-destructive/10'
  }
  if (contribution === 'required') {
    return 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40'
  }
  if (contribution === 'conflict' || contribution === 'needed') {
    return 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
  }
  if (contribution === 'checkedClear') {
    return 'border-border bg-muted/30'
  }
  return 'border-muted bg-muted/40'
}

export function clauseStateBadgeVariant(
  state: LawQuestionnaireClauseState
): 'success' | 'danger' | 'warning' {
  if (state === 'pass') {
    return 'success'
  }
  if (state === 'fail') {
    return 'danger'
  }
  return 'warning'
}

export function getPreferredLawQuestionnaireGroup(
  questions: LawQuestionnaireQuestion[],
  draft: LawQuestionnaireAnswerDraft,
  categoryRule: LawQuestionnaireCategoryRule
): LawQuestionnaireQuestionGroup | null {
  const grouped = groupLawQuestionnaireQuestions(questions)
  if (grouped.length === 0) {
    return null
  }

  for (const entry of grouped) {
    const hasWork = entry.questions.some((question) => {
      const contribution = getLawQuestionnaireContribution(
        question,
        questions,
        draft,
        categoryRule
      )
      return contribution === 'needed' || contribution === 'conflict'
    })
    if (hasWork) {
      return entry.group
    }
  }

  return grouped[0]?.group ?? null
}

export {
  getActiveLawQuestionnaireSection,
  getLawQuestionnaireSectionIntroKey,
  getLawQuestionnaireSectionPills,
  getLawQuestionnaireSectionState,
  getLawQuestionnaireSectionTitleKey,
  getLawQuestionnaireStatusBanner,
  getPreferredLawQuestionnaireGroupFromSections,
  isEng2317QuestionnaireLaw,
  sectionPillBadgeVariant,
  type LawQuestionnaireSectionCopy,
  type LawQuestionnaireSectionPill,
  type LawQuestionnaireSectionState,
  type LawQuestionnaireStatusBanner,
} from './lawApplicabilityQuestionnaireSections'
